/**
 * Standard easing functions for animations.
 * Based on Robert Penner's easing equations.
 * @see http://robertpenner.com/easing/
 */

export type WarpingFunction = (inputFraction: number) => number;

// ============================================
// Easing Constants
// ============================================

/**
 * Elastic easing constants.
 * These control the oscillation behavior of elastic animations.
 */
const ELASTIC_PERIOD = 0.3;
const ELASTIC_PERIOD_IN_OUT = 0.45; // ELASTIC_PERIOD * 1.5
/** Derived from: period / (2π) * arcsin(1) */
const ELASTIC_SHIFT = 0.075;
const ELASTIC_SHIFT_IN_OUT = 0.1125;

/**
 * Back easing overshoot constant.
 * Controls how much the animation "overshoots" before settling.
 * Standard value from Penner's equations (~10% overshoot).
 */
const BACK_OVERSHOOT = 1.70158;
/** Multiplier for back ease-in-out to increase overshoot effect */
const BACK_OVERSHOOT_MULTIPLIER = 1.525;

/**
 * Bounce easing physics constants.
 * Simulates a ball bouncing with decreasing height.
 */
const BOUNCE_GRAVITY = -12.5;
const BOUNCE_FIRST_BOUNCE_TIME = 0.4;
const BOUNCE_SECOND_BOUNCE_TIME = 0.75;
const BOUNCE_BASE_VELOCITY = 6.25;
const BOUNCE_FIRST_DURATION = 0.35;
const BOUNCE_SECOND_DURATION = 0.25;

export enum FractionWarperCurve {
  Unknown = 'Unknown',
  Linear = 'Linear',
  SinusoidalEaseIn = 'SinusoidalEaseIn',
  SinusoidalEaseOut = 'SinusoidalEaseOut',
  SinusoidalEaseInEaseOut = 'SinusoidalEaseInEaseOut',
  QuadraticEaseIn = 'QuadraticEaseIn',
  QuadraticEaseOut = 'QuadraticEaseOut',
  QuadraticEaseInEaseOut = 'QuadraticEaseInEaseOut',
  CubicEaseIn = 'CubicEaseIn',
  CubicEaseOut = 'CubicEaseOut',
  CubicEaseInEaseOut = 'CubicEaseInEaseOut',
  QuarticEaseIn = 'QuarticEaseIn',
  QuarticEaseOut = 'QuarticEaseOut',
  QuarticEaseInEaseOut = 'QuarticEaseInEaseOut',
  QuinticEaseIn = 'QuinticEaseIn',
  QuinticEaseOut = 'QuinticEaseOut',
  QuinticEaseInEaseOut = 'QuinticEaseInEaseOut',
  ExponentialEaseIn = 'ExponentialEaseIn',
  ExponentialEaseOut = 'ExponentialEaseOut',
  ExponentialEaseInEaseOut = 'ExponentialEaseInEaseOut',
  ElasticEaseIn = 'ElasticEaseIn',
  ElasticEaseOut = 'ElasticEaseOut',
  ElasticEaseInEaseOut = 'ElasticEaseInEaseOut',
  BackEaseIn = 'BackEaseIn',
  BackEaseOut = 'BackEaseOut',
  BackEaseInEaseOut = 'BackEaseInEaseOut',
  BounceEaseIn = 'BounceEaseIn',
  BounceEaseOut = 'BounceEaseOut',
  BounceEaseInEaseOut = 'BounceEaseInEaseOut',
}

class WarpingFunctionRegistry {
  private static registry: Map<FractionWarperCurve, WarpingFunction> | null = null;

  public static getRegistry(): Map<FractionWarperCurve, WarpingFunction> {
    if (this.registry === null) {
      this.registry = new Map<FractionWarperCurve, WarpingFunction>();

      // Linear
      this.registry.set(FractionWarperCurve.Linear, (fraction: number) => fraction);

      // Sinusoidal
      this.registry.set(FractionWarperCurve.SinusoidalEaseIn, (fraction: number) => 1.0 - Math.cos(fraction * (Math.PI / 2)));
      this.registry.set(FractionWarperCurve.SinusoidalEaseOut, (fraction: number) => Math.sin(fraction * (Math.PI / 2.0)));
      this.registry.set(FractionWarperCurve.SinusoidalEaseInEaseOut, (fraction: number) => (1.0 - Math.cos(fraction * Math.PI)) / 2.0);

      // Quadratic
      this.registry.set(FractionWarperCurve.QuadraticEaseIn, (fraction: number) => fraction * fraction);
      this.registry.set(FractionWarperCurve.QuadraticEaseOut, (fraction: number) => -fraction * (fraction - 2));
      this.registry.set(FractionWarperCurve.QuadraticEaseInEaseOut, (fraction: number) => {
        let t = fraction * 2.0;
        if (t < 1) {
          return 0.5 * t * t;
        }
        t -= 1.0;
        return -0.5 * (t * (t - 2.0) - 1.0);
      });

      // Cubic
      this.registry.set(FractionWarperCurve.CubicEaseIn, (fraction: number) => fraction * fraction * fraction);
      this.registry.set(FractionWarperCurve.CubicEaseOut, (fraction: number) => {
        const t = fraction - 1.0;
        return t * t * t + 1;
      });
      this.registry.set(FractionWarperCurve.CubicEaseInEaseOut, (fraction: number) => {
        let t = fraction * 2.0;
        if (t < 1) {
          return 0.5 * t * t * t;
        }
        t -= 2.0;
        return 0.5 * (t * t * t + 2.0);
      });

      // Quartic
      this.registry.set(FractionWarperCurve.QuarticEaseIn, (fraction: number) => fraction * fraction * fraction * fraction);
      this.registry.set(FractionWarperCurve.QuarticEaseOut, (fraction: number) => {
        const t = fraction - 1.0;
        return -1.0 * (t * t * t * t - 1);
      });
      this.registry.set(FractionWarperCurve.QuarticEaseInEaseOut, (fraction: number) => {
        let t = fraction * 2.0;
        if (t < 1) {
          return 0.5 * t * t * t * t;
        }
        t -= 2.0;
        return -0.5 * (t * t * t * t - 2.0);
      });

      // Quintic
      this.registry.set(FractionWarperCurve.QuinticEaseIn, (fraction: number) => fraction * fraction * fraction * fraction * fraction);
      this.registry.set(FractionWarperCurve.QuinticEaseOut, (fraction: number) => {
        const t = fraction - 1.0;
        return t * t * t * t * t + 1;
      });
      this.registry.set(FractionWarperCurve.QuinticEaseInEaseOut, (fraction: number) => {
        let t = fraction * 2.0;
        if (t < 1) {
          return 0.5 * t * t * t * t * t;
        }
        t -= 2.0;
        return 0.5 * (t * t * t * t * t + 2.0);
      });

      // Exponential
      this.registry.set(FractionWarperCurve.ExponentialEaseIn, (fraction: number) => Math.pow(2, 10 * (fraction - 1.0)));
      this.registry.set(FractionWarperCurve.ExponentialEaseOut, (fraction: number) => -Math.pow(2, -10 * fraction) + 1);
      this.registry.set(FractionWarperCurve.ExponentialEaseInEaseOut, (fraction: number) => {
        let t = 2.0 * fraction;
        if (t < 1.0) {
          return 0.5 * Math.pow(2, 10 * (t - 1));
        }
        t -= 1.0;
        return 0.5 * (-Math.pow(2, -10 * t) + 2);
      });

      // Elastic
      this.registry.set(FractionWarperCurve.ElasticEaseIn, (fraction: number) => {
        if (fraction === 0) return 0;
        if (fraction === 1) return 1;

        const n = fraction - 1.0;
        return -(Math.pow(2, 10 * n) * Math.sin(((n - ELASTIC_SHIFT) * (2 * Math.PI)) / ELASTIC_PERIOD));
      });
      this.registry.set(FractionWarperCurve.ElasticEaseOut, (fraction: number) => {
        if (fraction === 0) return 0;
        if (fraction === 1) return 1;

        const n = fraction;
        return Math.pow(2, -10 * n) * Math.sin(((n - ELASTIC_SHIFT) * (2 * Math.PI)) / ELASTIC_PERIOD) + 1;
      });
      this.registry.set(FractionWarperCurve.ElasticEaseInEaseOut, (fraction: number) => {
        let n = fraction;
        if (n === 0) return 0;
        if (n === 1) return 1;
        n *= 2.0;
        if (n < 1) {
          n -= 1.0;
          return -0.5 * (Math.pow(2, 10 * n) * Math.sin(((n - ELASTIC_SHIFT_IN_OUT) * (2 * Math.PI)) / ELASTIC_PERIOD_IN_OUT));
        }

        n -= 1.0;
        return Math.pow(2, -10 * n) * Math.sin(((n - ELASTIC_SHIFT_IN_OUT) * (2 * Math.PI)) / ELASTIC_PERIOD_IN_OUT) * 0.5 + 1;
      });

      // Back
      this.registry.set(FractionWarperCurve.BackEaseIn, (fraction: number) => {
        const n = fraction;
        if (n === 0) return 0;
        if (n === 1) return 1;
        return n * n * ((BACK_OVERSHOOT + 1) * n - BACK_OVERSHOOT);
      });
      this.registry.set(FractionWarperCurve.BackEaseOut, (fraction: number) => {
        let n = fraction;
        if (n === 0) return 0;
        if (n === 1) return 1;
        n -= 1.0;
        return n * n * ((BACK_OVERSHOOT + 1) * n + BACK_OVERSHOOT) + 1;
      });
      this.registry.set(FractionWarperCurve.BackEaseInEaseOut, (fraction: number) => {
        let n = fraction;
        if (n === 0) return 0;
        if (n === 1) return 1;
        const s = BACK_OVERSHOOT * BACK_OVERSHOOT_MULTIPLIER;
        n *= 2.0;
        if (n < 1) {
          return 0.5 * (n * n * ((s + 1) * n - s));
        }
        n -= 2.0;
        return 0.5 * (n * n * ((s + 1) * n + s) + 2);
      });

      // Bounce - BounceEaseOut is the base implementation
      this.registry.set(FractionWarperCurve.BounceEaseOut, (fraction: number) => {
        let t = fraction;
        if (t === 0) return 0;
        if (t === 1) return 1;
        let x0 = 0.0;
        let v0 = 0.0;
        if (t < BOUNCE_FIRST_BOUNCE_TIME) {
          x0 = 1.0;
          v0 = 0.0;
        } else if (t < BOUNCE_SECOND_BOUNCE_TIME) {
          t -= BOUNCE_FIRST_BOUNCE_TIME;
          x0 = 0.0;
          v0 = BOUNCE_BASE_VELOCITY * BOUNCE_FIRST_DURATION;
        } else {
          t -= BOUNCE_SECOND_BOUNCE_TIME;
          x0 = 0.0;
          v0 = BOUNCE_BASE_VELOCITY * BOUNCE_SECOND_DURATION;
        }
        return 1.0 - (x0 + v0 * t + 0.5 * BOUNCE_GRAVITY * t * t);
      });

      // BounceEaseIn uses BounceEaseOut
      this.registry.set(FractionWarperCurve.BounceEaseIn, (fraction: number) => {
        const bounceOut = this.registry!.get(FractionWarperCurve.BounceEaseOut)!;
        return 1.0 - bounceOut(1 - fraction);
      });

      // BounceEaseInEaseOut uses both BounceEaseIn and BounceEaseOut
      this.registry.set(FractionWarperCurve.BounceEaseInEaseOut, (fraction: number) => {
        const n = fraction;
        if (n < 0.5) {
          const bounceIn = this.registry!.get(FractionWarperCurve.BounceEaseIn)!;
          return bounceIn(n * 2) * 0.5;
        }
        const bounceOut = this.registry!.get(FractionWarperCurve.BounceEaseOut)!;
        return bounceOut(n * 2 - 1) * 0.5 + 0.5;
      });
    }
    return this.registry;
  }
}

export class WarpingFunctionMapUtil {
  public static getCurveTypeKeys(): FractionWarperCurve[] {
    const registry = WarpingFunctionRegistry.getRegistry();
    return Array.from(registry.keys());
  }

  public static getWarpingFunction(curveType: FractionWarperCurve): WarpingFunction | undefined {
    const registry = WarpingFunctionRegistry.getRegistry();
    return registry.get(curveType);
  }

  public static getWarpingFunctionName(curveType: FractionWarperCurve): string {
    return curveType;
  }

  public static getWarpingFunctionCurveType(name: string): FractionWarperCurve {
    // Try to find matching enum value
    const curveType = FractionWarperCurve[name as keyof typeof FractionWarperCurve];
    return curveType || FractionWarperCurve.Unknown;
  }
}
